import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { api, mediaUrl, wsUrl } from "@/lib/api";

const CACHE_KEY = (id) => `hm_display_${id}`;

export default function Display() {
  const { tvId } = useParams();
  const [data, setData] = useState(null);
  const [index, setIndex] = useState(0);
  const [now, setNow] = useState(new Date());
  const [error, setError] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  const wsRef = useRef(null);
  const timerRef = useRef(null);

  const fetchContent = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/display/${tvId}`);
      setData(d);
      setError("");
      localStorage.setItem(CACHE_KEY(tvId), JSON.stringify(d));
    } catch (e) {
      const status = e.response?.status;
      const cached = localStorage.getItem(CACHE_KEY(tvId));
      if (status === 404) {
        // Confirmed: this TV id does not exist. Only fatal case.
        setError("This TV is not registered. Register it in the admin panel.");
      } else if (cached) {
        // Transient error (502/5xx/network) -> keep showing cached content, retry later.
        setData(JSON.parse(cached));
        setError("");
        setTimeout(fetchContent, 5000);
      } else {
        // No cache yet and transient failure -> keep retrying, no fatal error.
        setTimeout(fetchContent, 5000);
      }
    }
  }, [tvId]);

  // Clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Online/offline
  useEffect(() => {
    const on = () => { setOnline(true); fetchContent(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [fetchContent]);

  // Initial fetch + WebSocket realtime
  useEffect(() => {
    fetchContent();
    let alive = true;
    let hb;
    const connect = () => {
      if (!alive) return;
      try {
        const ws = new WebSocket(wsUrl(tvId));
        wsRef.current = ws;
        ws.onopen = () => {
          hb = setInterval(() => { try { ws.send("ping"); } catch (e) {} }, 20000);
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "reload") { setIndex(0); fetchContent(); }
            if (msg.type === "restart") window.location.reload();
          } catch (e) {}
        };
        ws.onclose = () => { clearInterval(hb); if (alive) setTimeout(connect, 4000); };
        ws.onerror = () => { try { ws.close(); } catch (e) {} };
      } catch (e) {
        setTimeout(connect, 4000);
      }
    };
    connect();
    return () => { alive = false; clearInterval(hb); if (wsRef.current) wsRef.current.close(); };
  }, [tvId, fetchContent]);

  const media = data?.items || [];
  const s = data?.settings || {};
  const infoSlides = [];
  if (s.show_kajian && (data?.kajian?.length > 0)) infoSlides.push({ type: "kajian", id: "slide-kajian", duration: 15 });
  if (s.show_kas && data?.kas) infoSlides.push({ type: "kas", id: "slide-kas", duration: 15 });

  // Scheduled posters: active when today's weekday + current time within [start,end]
  const DOW_ID = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
  const todayName = DOW_ID[now.getDay()];
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
  const posterSlides = (data?.posters || [])
    .filter((p) => p.enabled && (p.days || []).includes(todayName) && nowMin >= toMin(p.start_time) && nowMin <= toMin(p.end_time))
    .map((p) => ({ type: "poster", id: "poster-" + p.id, media_id: p.media_id, duration: p.duration || 12 }));

  const items = [...media, ...posterSlides, ...infoSlides];
  const current = items[index % Math.max(items.length, 1)];
  const ADZAN_KEYS = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
  let adzanActive = null;
  if (s.show_adzan && data?.prayer?.timings) {
    const durMs = (Number(s.adzan_duration) || 5) * 60 * 1000;
    for (const k of ADZAN_KEYS) {
      const t = data.prayer.timings[k];
      if (!t) continue;
      const start = toDateToday(t, now);
      if (now >= start && now - start < durMs) adzanActive = { key: k, time: t };
    }
  }

  // Preload next images
  useEffect(() => {
    (data?.items || []).forEach((it) => { if (it.type === "image") { const img = new Image(); img.src = mediaUrl(it.id); } });
    (data?.posters || []).forEach((p) => { const img = new Image(); img.src = mediaUrl(p.media_id); });
  }, [data]);

  // Slideshow advance (images/info use duration; videos advance on end)
  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!current || items.length === 0) return;
    if (current.type !== "video") {
      timerRef.current = setTimeout(() => setIndex((i) => (i + 1) % items.length), (current.duration || 10) * 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [index, data]);

  const settings = data?.settings || {};
  const timeStr = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const dateStr = now.toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "long" });

  if (error) {
    return (
      <div className="tv-display flex items-center justify-center text-center px-8">
        <div>
          <img src="/logo.jpg" alt="Masjid Pintar" className="h-40 w-auto mx-auto object-contain rounded-2xl bg-white p-3" />
          <p className="mt-6 text-xl text-white/70">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tv-display relative" data-testid="tv-display">
      {/* Media layer z-0 */}
      <div className="absolute inset-0 z-0 bg-black">
        <AnimatePresence mode="wait">
          {current && (
            <motion.div
              key={`${current.id}-${index}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9 }}
              className="absolute inset-0"
            >
              {current.type === "image" ? (
                <motion.img
                  src={mediaUrl(current.id)}
                  alt=""
                  className="w-full h-full object-cover"
                  initial={{ scale: 1 }}
                  animate={{ scale: 1.06 }}
                  transition={{ duration: (current.duration || 8) + 1, ease: "linear" }}
                />
              ) : current.type === "video" ? (
                <video
                  src={mediaUrl(current.id)}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  playsInline
                  onEnded={() => setIndex((i) => (i + 1) % items.length)}
                />
              ) : current.type === "kajian" ? (
                <KajianSlide items={data?.kajian || []} />
              ) : current.type === "poster" ? (
                <PosterSlide mediaId={current.media_id} />
              ) : (
                <KasSlide kas={data?.kas} />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {items.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <img src="/logo.jpg" alt="Masjid Pintar" className="h-48 w-auto mx-auto object-contain rounded-2xl bg-white p-4" />
              <p className="mt-6 text-2xl text-white/60">Menunggu konten…</p>
            </div>
          </div>
        )}
      </div>

      {/* Gradient overlay z-10 */}
      <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-t from-black/90 via-black/10 to-black/40" />

      {/* Logo top-left z-20 */}
      <div className="absolute top-8 left-10 z-20 flex items-center gap-4">
        {settings.logo_media_id ? (
          <img src={mediaUrl(settings.logo_media_id)} alt="logo" className="h-16 max-w-[220px] object-contain drop-shadow-lg" />
        ) : (
          <img src="/logo.jpg" alt="Masjid Pintar" className="h-20 w-auto object-contain rounded-xl bg-white/90 p-2 drop-shadow-lg" />
        )}
      </div>

      {/* Clock top-right z-20 */}
      <div className="absolute top-8 right-10 z-20 text-right">
        <p className="font-mono text-6xl font-bold text-white leading-none drop-shadow-lg tabular-nums">{timeStr}</p>
        <p className="text-white/70 text-lg mt-2 font-medium">{dateStr}</p>
      </div>

      {/* Prayer times bar top-center z-20 */}
      {s.show_prayer_times && data?.prayer && (
        <PrayerBar prayer={data.prayer} now={now} />
      )}

      {/* Adzan overlay z-50 */}
      <AnimatePresence>
        {adzanActive && <AdzanOverlay prayerKey={adzanActive.key} time={adzanActive.time} timeStr={timeStr} />}
      </AnimatePresence>

      {/* Connection dot */}
      <div className="absolute bottom-28 right-10 z-20 flex items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${online ? "bg-[#10B981]" : "bg-[#ef4444]"}`} />
      </div>

      {/* Running text ticker z-30 */}
      {settings.ticker_text && (
        <div className="absolute bottom-0 inset-x-0 z-30 bg-gradient-to-t from-black via-black/90 to-transparent pt-8 pb-5">
          <div className="border-t-2 border-primary/60 pt-4">
            <Marquee gradient={false} speed={70} className="overflow-hidden">
              <span className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white px-8">
                {settings.ticker_text}
              </span>
              <span className="text-primary text-4xl md:text-5xl px-4">•</span>
            </Marquee>
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Prayer times bar (top-center) with next-prayer highlight + countdown
// ---------------------------------------------------------------------------
const PRAYER_ORDER = ["Imsak", "Fajr", "Sunrise", "Dhuhr", "Asr", "Maghrib", "Isha"];
const PRAYER_LABELS = { Imsak: "Imsak", Fajr: "Subuh", Sunrise: "Syuruq", Dhuhr: "Dzuhur", Asr: "Ashar", Maghrib: "Maghrib", Isha: "Isya" };

function toDateToday(hhmm, base) {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  const d = new Date(base);
  d.setHours(h, m, 0, 0);
  return d;
}

function PrayerBar({ prayer, now }) {
  const timings = prayer.timings || {};
  const order = PRAYER_ORDER.filter((k) => timings[k]);
  let nextKey = null;
  let nextAt = null;
  for (const k of order) {
    const t = toDateToday(timings[k], now);
    if (t > now) { nextKey = k; nextAt = t; break; }
  }
  let countdown = "";
  if (nextAt) {
    let diff = Math.max(0, Math.floor((nextAt - now) / 1000));
    const hh = String(Math.floor(diff / 3600)).padStart(2, "0");
    const mm = String(Math.floor((diff % 3600) / 60)).padStart(2, "0");
    const ss = String(diff % 60).padStart(2, "0");
    countdown = `${hh}:${mm}:${ss}`;
  }

  return (
    <div className="absolute top-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2">
      <div className="flex items-center gap-2 rounded-2xl bg-white/95 backdrop-blur-md px-3 py-2 shadow-2xl border border-primary/20">
        {order.map((k) => {
          const active = k === nextKey;
          return (
            <div key={k} className={`px-3 py-1.5 rounded-xl text-center min-w-[74px] ${active ? "bg-primary text-white" : "text-[#0a1f14]"}`} data-testid={`prayer-${k}`}>
              <p className={`text-[11px] uppercase tracking-wider ${active ? "text-white/90" : "text-primary"}`}>{PRAYER_LABELS[k]}</p>
              <p className="font-mono font-bold text-xl tabular-nums leading-tight">{timings[k]}</p>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 text-white drop-shadow-lg">
        <span className="text-sm text-white/80">{prayer.hijri}</span>
        {nextKey && (
          <span className="text-sm font-semibold bg-primary/90 rounded-full px-3 py-1">
            Menuju {PRAYER_LABELS[nextKey]} • {countdown}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Full-screen info slides (Pengajian & Kas) — branded green/white
// ---------------------------------------------------------------------------
const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

function PosterSlide({ mediaId }) {
  const url = mediaUrl(mediaId);
  return (
    <div className="w-full h-full relative bg-black overflow-hidden">
      <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40" />
      <div className="absolute inset-0 flex items-center justify-center p-6">
        <img src={url} alt="Poster" className="max-w-full max-h-full object-contain drop-shadow-2xl" />
      </div>
    </div>
  );
}


function KajianSlide({ items }) {
  return (
    <div className="w-full h-full bg-white flex flex-col items-center justify-center px-20 py-24">
      <p className="text-primary text-2xl uppercase tracking-[0.3em] font-semibold">Jadwal Pengajian Rutin</p>
      <div className="mt-10 w-full max-w-5xl space-y-4">
        {items.slice(0, 6).map((k) => (
          <div key={k.id} className="flex items-center gap-6 bg-secondary rounded-2xl px-8 py-5 border border-primary/10">
            <div className="text-center min-w-[150px] bg-primary text-white rounded-xl px-5 py-3">
              <p className="text-lg uppercase tracking-wider">{k.day}</p>
              <p className="font-mono text-3xl font-bold">{k.time}</p>
            </div>
            <div className="flex-1 text-left">
              <p className="font-display text-3xl font-bold text-[#0a1f14] leading-tight">{k.title}</p>
              <p className="text-xl text-[#0a1f14]/70 mt-1">
                {k.ustadz && <span>{k.ustadz}</span>}
                {k.ustadz && k.location && <span> · </span>}
                {k.location && <span>{k.location}</span>}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function KasSlide({ kas }) {
  const cards = [
    { label: "Total Pemasukan", value: kas?.pemasukan, cls: "text-emerald-600" },
    { label: "Saldo Kas", value: kas?.saldo, cls: "text-primary" },
    { label: "Total Pengeluaran", value: kas?.pengeluaran, cls: "text-rose-600" },
  ];
  return (
    <div className="w-full h-full bg-white flex flex-col items-center justify-center px-20">
      <p className="text-primary text-2xl uppercase tracking-[0.3em] font-semibold">Laporan Kas Masjid</p>
      <div className="mt-12 grid grid-cols-3 gap-8 w-full max-w-6xl">
        {cards.map((c) => (
          <div key={c.label} className="bg-secondary rounded-3xl border border-primary/10 px-8 py-12 text-center">
            <p className="text-xl uppercase tracking-widest text-[#0a1f14]/60">{c.label}</p>
            <p className={`mt-6 font-display text-5xl font-extrabold tabular-nums ${c.cls}`}>{rupiah(c.value)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Adzan overlay — full screen when a prayer time arrives
// ---------------------------------------------------------------------------
function AdzanOverlay({ prayerKey, time, timeStr }) {
  const label = PRAYER_LABELS[prayerKey] || prayerKey;
  return (
    <motion.div
      className="absolute inset-0 z-50 flex flex-col items-center justify-center text-center px-10"
      style={{ background: "linear-gradient(160deg, #0a1f14 0%, #0f5132 55%, #16a34a 100%)" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
      data-testid="adzan-overlay"
    >
      <motion.div
        className="absolute h-[42rem] w-[42rem] rounded-full bg-white/5"
        animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.15, 0.4] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        initial={{ scale: 0.85, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="relative flex flex-col items-center"
      >
        <img src="/logo.jpg" alt="Masjid Pintar" className="h-40 w-auto object-contain rounded-2xl bg-white p-3 shadow-2xl" />
        <p className="mt-10 text-white/80 text-3xl tracking-[0.3em] uppercase">Telah Masuk Waktu Shalat</p>
        <h1 className="mt-4 font-display text-8xl font-extrabold text-white drop-shadow-2xl">{label}</h1>
        <p className="mt-4 font-mono text-6xl font-bold text-white/95 tabular-nums">{time}</p>
        <p className="mt-10 text-2xl text-white/70">Mari tunaikan shalat berjamaah tepat waktu</p>
        <p className="mt-2 text-lg text-white/50 font-mono">{timeStr}</p>
      </motion.div>
    </motion.div>
  );
}
